/**
 * AnimatedDogAvatar — DogAvatar (warstwy stroju) + animacja chodu/akcji.
 * Kontrakt PLAN-V2 §2.2 + WAVE5 W3 (idle breath, walk cycle, happy, reduced-motion).
 * Gra nigdy nie czeka na assety (fallback proceduralny). Ruch świata nie blokuje się na akcji.
 */
import Phaser from 'phaser';
import { DogAvatar, type DogAvatarOpts } from './DogAvatar.js';
import type { OutfitSlots, SwimSlots } from './parseOutfit.js';
import {
  createAnimMachine,
  setMoving,
  setMoveSpeed,
  setReducedMotion,
  startAction,
  playIdle as machinePlayIdle,
  stepAnim,
  detectPrefersReducedMotion,
  MOVE_SPEED_THRESHOLD,
  type AnimMachineState,
  type AvatarAction,
} from './animMachine.js';
import {
  parseAnchorsFile,
  resolveAnchors,
  anchorToLocal,
  type AnchorPose,
} from './anchors.js';
import {
  createHoldItemState,
  finishHoldTransition,
  holdItem as holdItemPure,
  holdTransform,
  heldItem as heldItemPure,
  stepHoldTransition,
  visualHeldItem,
  type HoldItemState,
} from './holdItem.js';
import { playItemTransitionFx } from '../interact/fx.js';

export type AnimatedDogAvatarOpts = DogAvatarOpts & {
  /** px/s — domyślnie ~DOG_SPEED feel. */
  walkSpeed?: number;
  /** Wstępnie sparsowane kotwice (testy / preload). */
  anchorsData?: Record<string, Record<string, Partial<AnchorPose>>> | null;
  /**
   * Wymuś reduced-motion (testy / ustawienia).
   * Domyślnie: detectPrefersReducedMotion() z OS/przeglądarki.
   */
  reducedMotion?: boolean;
};

const DEFAULT_WALK_SPEED = 160;

/** Cache globalny anchors.json (fetch raz). */
let anchorsCache: Record<string, Record<string, Partial<AnchorPose>>> | null | undefined;
let anchorsFetch: Promise<void> | null = null;

export function preloadAnchors(scene?: Phaser.Scene): Promise<void> {
  if (anchorsCache !== undefined) return Promise.resolve();
  if (anchorsFetch) return anchorsFetch;
  anchorsFetch = (async () => {
    try {
      // Phaser cache jeśli załadowano jako json
      if (scene?.cache?.json?.exists('anchors')) {
        anchorsCache = parseAnchorsFile(scene.cache.json.get('anchors'));
        return;
      }
      const res = await fetch('/assets/real/anchors.json');
      if (!res.ok) {
        anchorsCache = null;
        return;
      }
      anchorsCache = parseAnchorsFile(await res.json());
    } catch {
      anchorsCache = null;
    }
  })();
  return anchorsFetch;
}

/** Test helper — wstrzyknij / wyczyść cache kotwic. */
export function __setAnchorsCacheForTests(
  data: Record<string, Record<string, Partial<AnchorPose>>> | null | undefined,
): void {
  anchorsCache = data;
  anchorsFetch = null;
}

export class AnimatedDogAvatar {
  readonly container: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private readonly dog: DogAvatar;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly juiceRoot: Phaser.GameObjects.Container;
  private breed: string;
  private walkSpeed: number;
  private machine: AnimMachineState;
  private hold: HoldItemState;
  private heldNode: Phaser.GameObjects.GameObject | null = null;
  private heldBaseScaleX = 1;
  private heldBaseScaleY = 1;
  private moveTarget: { x: number; y: number } | null = null;
  private velocity = { x: 0, y: 0 };
  private anchorsData: Record<string, Record<string, Partial<AnchorPose>>> | null;
  private actionWaiters: Array<() => void> = [];
  private dustCooldown = 0;
  private lastPose = '';
  private lastFlip = false;
  private reducedMotion: boolean;

  constructor(opts: AnimatedDogAvatarOpts) {
    this.scene = opts.scene;
    this.breed = opts.breed ?? 'maltese';
    this.walkSpeed = opts.walkSpeed ?? DEFAULT_WALK_SPEED;
    this.reducedMotion =
      opts.reducedMotion !== undefined ? opts.reducedMotion : detectPrefersReducedMotion();
    this.machine = createAnimMachine({ reducedMotion: this.reducedMotion });
    this.hold = createHoldItemState();
    this.anchorsData =
      opts.anchorsData !== undefined
        ? opts.anchorsData
        : anchorsCache === undefined
          ? null
          : anchorsCache;

    this.container = this.scene.add.container(opts.x, opts.y);
    if (opts.depth != null) this.container.setDepth(opts.depth);

    this.shadow = this.scene.add.ellipse(0, 36, 48, 16, 0x000000, 0.18);
    this.juiceRoot = this.scene.add.container(0, 0);

    this.dog = new DogAvatar({
      ...opts,
      x: 0,
      y: 0,
      depth: undefined,
    });
    // reparent dog do juiceRoot
    this.juiceRoot.add(this.dog.container);
    this.dog.container.setPosition(0, 0);

    this.container.add([this.shadow, this.juiceRoot]);
    this.container.setScale(1);

    // async kotwice — bez blokady
    if (opts.anchorsData === undefined && anchorsCache === undefined) {
      void preloadAnchors(this.scene).then(() => {
        if (anchorsCache !== undefined) {
          this.anchorsData = anchorsCache;
          this.syncVisuals(true);
        }
      });
    }

    this.syncVisuals(true);
  }

  get x(): number {
    return this.container.x;
  }
  set x(v: number) {
    this.container.x = v;
  }
  get y(): number {
    return this.container.y;
  }
  set y(v: number) {
    this.container.y = v;
  }

  get isMoving(): boolean {
    if (Math.hypot(this.velocity.x, this.velocity.y) > MOVE_SPEED_THRESHOLD) return true;
    if (this.moveTarget) {
      const dx = this.moveTarget.x - this.container.x;
      const dy = this.moveTarget.y - this.container.y;
      return Math.hypot(dx, dy) > 2;
    }
    return false;
  }

  get heldItem(): string | null {
    return heldItemPure(this.hold);
  }

  /** Wewnętrzny DogAvatar (sceny V1 mogą czytać strój). */
  get dogAvatar(): DogAvatar {
    return this.dog;
  }

  /** Aktualna flaga reduced-motion (OS lub wymuszona). */
  get prefersReducedMotion(): boolean {
    return this.reducedMotion;
  }

  /** Runtime toggle (ustawienia / testy). */
  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    this.machine = setReducedMotion(this.machine, reduced);
    if (reduced) {
      const previousVisual = visualHeldItem(this.hold);
      this.hold = finishHoldTransition(this.hold);
      if (previousVisual !== visualHeldItem(this.hold)) this.rebuildHeldNode();
    }
  }

  moveTo(x: number, y: number): void {
    this.moveTarget = { x, y };
    this.velocity = { x: 0, y: 0 };
  }

  setVelocity(vx: number, vy: number): void {
    this.velocity = { x: vx, y: vy };
    if (Math.hypot(vx, vy) > MOVE_SPEED_THRESHOLD) {
      this.moveTarget = null;
    }
  }

  stop(): void {
    this.moveTarget = null;
    this.velocity = { x: 0, y: 0 };
  }

  playAction(a: AvatarAction): Promise<void> {
    this.machine = startAction(this.machine, a);
    this.syncVisuals(true);
    if (!this.reducedMotion && (a === 'happy' || a === 'eat')) {
      this.burstSparkles(a === 'happy' ? 0xff6b8a : 0xffd700);
    }
    return new Promise((resolve) => {
      this.actionWaiters.push(resolve);
    });
  }

  playIdle(): void {
    this.machine = machinePlayIdle(this.machine);
    this.flushActionWaiters();
    this.syncVisuals(true);
  }

  setOutfit(slots: OutfitSlots, swimSlots?: SwimSlots): void {
    this.dog.setOutfit(slots, swimSlots);
    this.syncVisuals(true);
  }

  setMode(m: 'normal' | 'swim'): void {
    this.dog.setMode(m);
    this.syncVisuals(true);
  }

  setBreed(breed: string, color?: string): void {
    this.breed = breed;
    this.dog.setBreed(breed, color);
    this.syncVisuals(true);
  }

  holdItem(
    textureKey: string | null,
    opts?: { animate?: boolean },
  ): void {
    const previousVisual = visualHeldItem(this.hold);
    const previousState = this.hold;
    this.hold = holdItemPure(previousState, textureKey);
    const animate = opts?.animate !== false && !this.reducedMotion;
    if (!animate) this.hold = finishHoldTransition(this.hold);
    const changed = this.hold !== previousState;
    if (!changed) return;
    this.rebuildHeldNode();
    this.syncVisuals(true);
    if (animate) {
      const kind =
        textureKey != null && textureKey !== ''
          ? 'pickup'
          : previousVisual
            ? 'drop'
            : null;
      if (kind) {
        const anchors = resolveAnchors(
          this.anchorsData,
          this.breed,
          this.machine.pose,
        );
        const mouth = anchorToLocal(anchors, 'mouth', this.facingLeft());
        playItemTransitionFx(
          this.scene,
          kind,
          this.container.x + mouth.x,
          this.container.y + mouth.y + this.machine.juice.bobY,
          { depth: (this.container.depth || 0) + 6 },
        );
      }
    }
  }

  /** Wstrzyknij / odśwież kotwice (kalibrator ?anchors=1). */
  setAnchorsData(
    data: Record<string, Record<string, Partial<AnchorPose>>> | null,
  ): void {
    this.anchorsData = data;
    this.syncVisuals(true);
  }

  /**
   * Pętla — wołaj z scene.update (dt w sekundach).
   * Ruch kontenera zawsze. machine.moving = realny isMoving także podczas akcji
   * (stepAnim i tak trzyma pozę akcji; po końcu wraca od razu do walk/idle bez klatki idle).
   */
  update(dtSec: number): void {
    const dt = Math.max(0, Math.min(dtSec, 0.05)); // clamp spiral of death
    this.stepMovement(dt);

    // Nie gaś moving na czas akcji — inaczej po happy miga idle zanim wróci chód.
    const moving = this.isMoving;
    this.machine = setMoveSpeed(this.machine, moving ? this.movementSpeed() : 0);
    this.machine = setMoving(this.machine, moving);

    const prevAction = this.machine.action;
    this.machine = stepAnim(this.machine, dt);
    if (prevAction && !this.machine.action) {
      this.flushActionWaiters();
    }

    const previousHeldVisual = visualHeldItem(this.hold);
    this.hold = this.reducedMotion
      ? finishHoldTransition(this.hold)
      : stepHoldTransition(this.hold, dt);
    if (previousHeldVisual !== visualHeldItem(this.hold)) {
      this.rebuildHeldNode();
    }

    // kurz (wyłączony w reduced-motion przez juice.dust === false)
    if (this.machine.juice.dust) {
      this.dustCooldown -= dt;
      if (this.dustCooldown <= 0) {
        this.spawnDust();
        this.dustCooldown = 0.12;
      }
    }

    if (this.machine.juice.sparkle > 0.5 && Math.random() < dt * 8) {
      this.burstSparkles(0xff9ec8, 1);
    }

    this.applyJuice();
    this.syncVisuals(false);
  }

  destroy(): void {
    this.flushActionWaiters();
    this.heldNode?.destroy();
    this.dog.destroy();
    this.container.destroy(true);
  }

  private stepMovement(dt: number): void {
    let vx = this.velocity.x;
    let vy = this.velocity.y;
    const speed = Math.hypot(vx, vy);

    if (speed > MOVE_SPEED_THRESHOLD) {
      this.container.x += vx * dt;
      this.container.y += vy * dt;
      return;
    }

    if (this.moveTarget) {
      const dx = this.moveTarget.x - this.container.x;
      const dy = this.moveTarget.y - this.container.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 2) {
        this.container.x = this.moveTarget.x;
        this.container.y = this.moveTarget.y;
        this.moveTarget = null;
        return;
      }
      const step = Math.min(dist, this.walkSpeed * dt);
      this.container.x += (dx / dist) * step;
      this.container.y += (dy / dist) * step;
    }
  }

  private facingLeft(): boolean {
    if (Math.abs(this.velocity.x) > MOVE_SPEED_THRESHOLD) return this.velocity.x < 0;
    if (this.moveTarget) {
      return this.moveTarget.x < this.container.x - 1;
    }
    return this.lastFlip;
  }

  private movementSpeed(): number {
    const velocitySpeed = Math.hypot(this.velocity.x, this.velocity.y);
    if (velocitySpeed > MOVE_SPEED_THRESHOLD) return velocitySpeed;
    return this.moveTarget ? this.walkSpeed : 0;
  }

  private syncVisuals(force: boolean): void {
    const pose = this.machine.pose;
    const flip = this.facingLeft();
    // tylko przy zmianie pozy — bez migotania co klatkę
    if (force || pose !== this.lastPose) {
      this.dog.setPose(pose);
      this.lastPose = pose;
    }
    const anchors = resolveAnchors(this.anchorsData, this.breed, pose);
    this.dog.layoutWithAnchors(anchors, flip);
    this.lastFlip = flip;
    this.layoutHeld(anchors, flip);
  }

  private applyJuice(): void {
    const j = this.machine.juice;
    this.juiceRoot.setPosition(0, j.bobY);
    this.juiceRoot.setScale(j.scaleX, j.scaleY);
    this.juiceRoot.setAngle(j.tiltDeg);
    this.shadow.setScale(j.shadowScaleX, j.shadowScaleY);
    this.shadow.setAlpha(0.12 + 0.08 * j.shadowScaleX);
  }

  private rebuildHeldNode(): void {
    this.heldNode?.destroy();
    this.heldNode = null;
    this.heldBaseScaleX = 1;
    this.heldBaseScaleY = 1;
    const key = visualHeldItem(this.hold);
    if (!key) return;

    if (this.scene.textures.exists(key)) {
      const img = this.scene.add.image(0, 0, key);
      const frame = this.scene.textures.getFrame(key);
      const sourceW = Math.max(1, frame?.realWidth ?? frame?.width ?? 36);
      const sourceH = Math.max(1, frame?.realHeight ?? frame?.height ?? 36);
      const scale = Math.min(42 / sourceW, 46 / sourceH);
      img.setDisplaySize(sourceW * scale, sourceH * scale);
      this.heldBaseScaleX = img.scaleX;
      this.heldBaseScaleY = img.scaleY;
      this.heldNode = img;
    } else {
      // placeholder proceduralny — kolorowy kwadrat
      const rect = this.scene.add
        .rectangle(0, 0, 28, 28, 0xffb347, 0.95)
        .setStrokeStyle(2, 0xffffff);
      const t = this.scene.add
        .text(0, 0, '★', {
          fontSize: '16px',
          color: '#4a3f55',
        })
        .setOrigin(0.5);
      this.heldNode = this.scene.add.container(0, 0, [rect, t]);
    }
    this.juiceRoot.add(this.heldNode);
  }

  private layoutHeld(anchors: AnchorPose, flip: boolean): void {
    if (!this.heldNode) return;
    const local = anchorToLocal(anchors, 'mouth', flip);
    const transition = holdTransform(this.hold);
    const n = this.heldNode as unknown as {
      setAlpha?: (alpha: number) => void;
      setAngle?: (angle: number) => void;
      setPosition?: (x: number, y: number) => void;
      setScale?: (scaleX: number, scaleY?: number) => void;
    };
    n.setPosition?.(local.x, local.y + transition.offsetY);
    n.setScale?.(
      this.heldBaseScaleX * transition.scale,
      this.heldBaseScaleY * transition.scale,
    );
    n.setAlpha?.(transition.alpha);
    n.setAngle?.(transition.angleDeg * (flip ? -1 : 1));
  }

  private spawnDust(): void {
    if (this.reducedMotion) return;
    const d = this.scene.add.circle(
      this.container.x + (Math.random() - 0.5) * 20,
      this.container.y + 34,
      3 + Math.random() * 3,
      0xc4a882,
      0.45,
    );
    d.setDepth((this.container.depth || 0) - 1);
    this.scene.tweens.add({
      targets: d,
      alpha: 0,
      y: d.y - 12,
      scale: 1.6,
      duration: 280,
      onComplete: () => d.destroy(),
    });
  }

  private burstSparkles(color: number, count = 5): void {
    if (this.reducedMotion) return;
    for (let i = 0; i < count; i++) {
      const hx = this.container.x + (Math.random() - 0.5) * 40;
      const hy = this.container.y - 20 + (Math.random() - 0.5) * 30;
      const h = this.scene.add.circle(hx, hy, 4, color, 0.9);
      h.setDepth((this.container.depth || 0) + 5);
      this.scene.tweens.add({
        targets: h,
        y: hy - 30 - Math.random() * 20,
        alpha: 0,
        scale: 0.3,
        duration: 400 + Math.random() * 200,
        onComplete: () => h.destroy(),
      });
    }
  }

  private flushActionWaiters(): void {
    const w = this.actionWaiters;
    this.actionWaiters = [];
    for (const r of w) r();
  }
}

// re-export threshold for tests / callers that mirror movement logic
export { MOVE_SPEED_THRESHOLD };

export type { AvatarAction };
